/// <amd-module name="Build/Extensions/Bitbucket/RepositorySelection" />
import * as React from "react";

import { Props as BaseProps, RepositorySelector } from "Build/Designer/RepositorySelector";
import * as DesignerResources from "Build/Designer/Resources";
import * as Resources from "Build/Extensions/Bitbucket/Resources";

import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { IVssComponentContext } from "VSS/Platform/Layout";

interface ServiceConnectionInfo {
    id: string;
    manageAccessUrl: string;
}

interface ServiceConnectionData {
    serviceConnections: ServiceConnectionInfo[];
}

export interface Props extends BaseProps {
    serviceName: string;
    onErrorMessage: (errorMessage: string) => void;
}

export class RepositorySelection extends RepositorySelector<Props> {
    private _link: string;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.bitbucket-serviceconnections-data-provider";
        this.trackPromise(
            contributionService.getDataAsync<ServiceConnectionData>(dataProviderId, { "endpointType": this.props.sourceProvider }, true)
        ).promise.then((serviceConnectionData) => {
            if (serviceConnectionData) {
                const connection = serviceConnectionData.serviceConnections.find(
                    c => c.id === this.props.connectionId);
                if (connection) {
                    this._link = connection.manageAccessUrl;
                    this.setState({
                        footerText: this.getFooter(this.state.filterNoReposFound, !!(this.state.repositoryList && this.state.repositoryList.continuationToken))
                    });
                }
            } else {
                const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                this.props.onErrorMessage(exception && exception.message);
            }
        }).catch((error: Error) => {
            this.props.onErrorMessage(error && error.message);
        });
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this.props.onErrorMessage("");
    }

    protected autoSelectConnection(): boolean {
        return false;
    }

    protected getClassName(baseClassName: string): string {
        return baseClassName + " bitbucket";
    }

    protected getFooter(filterNoReposFound: boolean, downloading: boolean): JSX.Element | undefined {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const currentState = historyService.getState();

        ["connectionId", "repositoryId", "key"]
            .filter(x => currentState[x])
            .forEach(x => delete currentState[x]);

        const url: string = historyService.generateUrl({
            ...currentState,
            showSelectConnection: "true"
        }, StateMergeOptions.none)

        if (downloading) {
            return undefined;
        }

        if (filterNoReposFound) {
            return super.getFooter(filterNoReposFound, downloading);
        }

        const link = this.getFooterLink();
        return (<>
            {this._link ? Resources.MemberRepositoriesDescription : DesignerResources.RepositorySelectorNoneFoundDescription}
            {link && <div>
                    <FormatComponent format={Resources.BitbucketGrantPermissionDescription}>
                        <FPSLink href={link}
                            supportsFPS={false} // guaranteed to be an external link
                            telemetrySource={"new-pipeline-wizard.bitbucket-grant-permissions"}
                            telemetryProperties={{ telemetrySession: currentState.telemetrySession }}>
                            {Resources.BitbucketGrantPermissionDescriptionLink}
                        </FPSLink>
                    </FormatComponent>
                </div>
            }
            <FormatComponent format={Resources.SelectServiceConnectionMessage}>
                <FPSLink href={url}
                    telemetrySource={"new-pipeline-wizard.select-connection"}
                    telemetryProperties={{ telemetrySession: currentState.telemetrySession }}>
                    {Resources.SelectServiceConnectionMessageLink}
                </FPSLink>
            </FormatComponent>
        </>);
    }

    protected getFooterLink(): string | undefined {
        return this._link;
    }

    protected getRepositoryIconName(): string {
        return "BitbucketLogo32";
    }
}
